<template>
  <Card v-if="membership" class="mb-4">
    <template #title>
      <h4 class="d-flex mb-0">
        {{ membership.membership_program_name }}
        <img
          :src="membership.icon_url"
          alt="Membership Icon"
          class="ml-1 member-icon"
        />
        <i class="fas fa-edit link ml-2" @click="$emit('edit-membership')"></i>
      </h4>
    </template>
    <template #body>
      <div
        class="grid grid-flow-row md:grid-flow-col auto-cols-auto gap-4 divide-y *:pt-4 md:flex-row md:divide-x md:divide-y-0 *:md:pl-4 *:md:pt-0"
      >
        <div class="item-section md:-ml-4 -mt-4 md:mt-0">
          <div class="mb-1">
            <b>Starts On:</b> {{ shortDate(membership.starts_at) }}
          </div>
          <div v-if="membership.auto_renews" class="mb-1">
            <div v-if="membership.renews_at" class="mb-1">
              <b>Next Billing Date:</b> {{ shortDate(membership.renews_at) }}
            </div>
            <div v-else class="mb-1">
              <b>Next Billing Date:</b> <i style="color: red">Unconfirmed</i>
            </div>
          </div>
          <div v-if="membership.ends_at" class="membership-tag mb-1">
            <b>{{ endTag }}</b>
            {{ shortDate(membership.ends_at) }}
          </div>
          <div class="mb-1">
            <b>Covered System(s):</b> {{ membership.covered_systems || "--" }}
          </div>
          <div class="mb-1">
            <b>Service Address:</b>
            <template v-if="membership?.addresses?.length">
              <ul class="list-disc pl-5">
                <li v-for="address in membership.addresses" :key="address.id">
                  {{ fullAddress(address) }}
                </li>
              </ul>
            </template>
            <template v-else> --</template>
          </div>
          <div class="mb-1">
            <b>Billing price:</b> {{ dollars(membership.price_cents) }}
          </div>
          <div class="mb-1">
            <b>Duration:</b> {{ displayMembershipDuration(membership) }}
          </div>
        </div>
        <div class="item-section">
          <div>
            <div><b>Description</b></div>
            <div class="pre-line">{{ description }}</div>
          </div>
        </div>
        <div class="item-section flex-grow-2">
          <table v-if="items.length" class="mp-tasks-table">
            <thead>
              <tr>
                <th>Included Tasks</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item.item_id">
                <td>
                  {{ item.quantity }} {{ item.unit_name }}
                  {{ displayFrequency(item) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else>
            <div><b>Included Tasks</b></div>
            --
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import Card from "@/components/Card.vue";
import { dollars, fullAddress, shortDate } from "@/mixins";
import {
  displayFrequency,
  displayMembershipDuration
} from "@/mixins/membership";
import { CustomerMembership } from "@/typings/memberships";

const props = withDefaults(
  defineProps<{
    membership: CustomerMembership | null;
  }>(),
  { membership: null }
);

defineEmits(["edit-membership"]);

const store = useStore();

const programDetails = computed(() => {
  return store.state.membershipPrograms.find(
    (mp: { id: number }) => mp.id === props.membership?.membership_program_id
  );
});

const description = computed(() => {
  return programDetails.value ? programDetails.value.description : "--";
});

const items = computed(() => {
  return programDetails.value ? programDetails.value.items : [];
});

const endTag = computed(() => {
  return props.membership?.renews_at ? "Cancellation Date" : "Ends On";
});
</script>

<style lang="scss" scoped>
.flex-grow-2 {
  flex-grow: 2;
}
.mp-tasks-table {
  tbody tr {
    border-bottom: 2px solid var(--gray-border-color);
    td {
      padding: 5px 0;
    }
    &:last-of-type {
      border-bottom: none;
    }
  }
}
.expired-label {
  font-style: italic;
  color: var(--gray);
}

.link {
  font-size: 0.9rem;
}

.member-icon {
  height: 1em;
}
</style>
